import React, { useContext, useState, useEffect } from "react";
import { StateContext } from "../../utils/context";
import {
  Card,
  CardTitle,
  CardBody,
  Badge,
  Alert,
} from "reactstrap";
import HelpItem from "../../components/helpItem";
import { Link } from "react-router-dom";
import ZapierBot from "../../components/zapierBot";
import SLAPlans from "./slaPlans";

const MMD_GMBH_PRICING_TABLE_ID = process.env.REACT_APP_MMD_GMBH_PRICING_TABLE_ID;
const MMD_DOO_PRICING_TABLE_ID = process.env.REACT_APP_MMD_DOO_PRICING_TABLE_ID;
const MMD_GMBH_PRICING_TABLE_KEY = process.env.REACT_APP_MMD_GMBH_PRICING_TABLE_KEY;
const MMD_DOO_PRICING_TABLE_KEY = process.env.REACT_APP_MMD_DOO_PRICING_TABLE_KEY;


const ServiceLevelAgreement = () => {
  const { currentUser } = useContext(StateContext);
  const { sla_level, wl_customer_portal_url } = currentUser;

  const [pricingTableID, pricingTableIDSET] = useState("");
  const [pricingTableKey, pricingTableKeySET] = useState("");

  const learnMoreDetailsLink = "https://iq.movemusic.io/external/manual/sla-service-level-agreement-white-label-rest-api/article/faq?p=7379d1847d3c371eef7c1475515b28636c1343e4ea449eadf1b923f9143fc30f4c8ca585aef12c5fbd54e1c044ff358f";

  useEffect(() => {
    if (currentUser && currentUser.contract_owner) {
      let contractOwner = currentUser.contract_owner;    
      if (contractOwner === 1) {
        pricingTableIDSET(MMD_GMBH_PRICING_TABLE_ID);
        pricingTableKeySET(MMD_GMBH_PRICING_TABLE_KEY);
      } else if (contractOwner === 2) {
        pricingTableIDSET(MMD_DOO_PRICING_TABLE_ID);
        pricingTableKeySET(MMD_DOO_PRICING_TABLE_KEY);
      }
    }
  }, [currentUser]);

  const slaConfig = {
    'Standard': {
      name: 'Standard',
      bg: '#95A5A6',
      text: '#FFFFFF',
      icon: 'mdi-lifebuoy',
    },
    'Business Lite': {
      name: 'Business Lite',
      bg: '#0A8FE8',
      text: '#FFFFFF',
      icon: 'mdi-rocket-launch',
    },
    'Business': {
      name: 'Business',
      bg: '#1E8449',
      text: '#FFFFFF',
      icon: 'mdi-handshake-outline',
    },
    'Gold': {
      name: 'Gold',
      bg: '#F4D03F',
      text: '#000000',
      icon: 'mdi-diamond-stone',
    },
  };

  const config = slaConfig[sla_level] || {
    name: '',
    bg: '#C0C0C0',
    text: '#000000',
    icon: '',
  };

  return (
    <>
        <Card>
            <CardBody>
                <CardTitle tag="h3">Your SLA Level</CardTitle>

                <div style={{ display: 'flex', alignItems: 'center', maxWidth: '100%' }}>
                    <Badge
                        className="mr-3 p-2 d-flex align-items-center"
                        style={{
                            backgroundColor: config.bg,
                            color: config.text,
                        }}
                    >
                      <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
                          {config.name}
                      </span>
                      {config.icon && <i className={`ml-2 mdi ${config.icon}`}></i>}
                    </Badge>

                    <span style={{ fontSize: '14px' }}>
                        This page is NOT visible to your customers, it is only visible to your company admins.
                    </span>
                </div>

                <Alert color="info" className="mt-3">
                  <Link to={{ pathname: learnMoreDetailsLink }} target="_blank"
                      style={{
                        fontWeight: 'bold',
                      }}
                  >Learn more details</Link> about SLA levels.
                </Alert>

                {
                  wl_customer_portal_url && (
                  <Link to={{ pathname: wl_customer_portal_url }} target="_blank"
                      style={{
                        fontWeight: 'bold',
                      }}
                  >Customer Portal</Link>
                  )
                }

            </CardBody>
        </Card>

        <Card>
            <CardBody>
                <CardTitle tag="h3">SLA Plans</CardTitle>
                <Alert color="info">
                  If you have subscribed to a SLA plan for the first time or upgraded to a new SLA plan, do not buy it once again, please note that it will take upto <strong>3 business days</strong> to activate the SLA plan and full features to be activated.
                </Alert>
                {/* <stripe-pricing-table pricing-table-id={pricingTableID} publishable-key={pricingTableKey} customer-email={currentUser.wl_manager_email}>
                </stripe-pricing-table> */}
                <SLAPlans currentSLAName={currentUser.sla_level} hasSLAPlan={currentUser.has_sla_plan} />
            </CardBody>
        </Card>

        <Card>
            <CardBody>
                <CardTitle tag="h3">Frequently Asked Questions</CardTitle>
                <div>
                    <HelpItem heading="Do I need to subscribe to an SLA?">
                      Yes, you need to subscribe to an <strong>SLA (Service Level Agreement)</strong>, even if you choose the <strong>STANDARD (FREE)</strong> plan.<br/><br/>
                      You won’t be charged under the free plan, but activation is required to manage your support access and ensure proper service tracking.
                    </HelpItem>

                    <HelpItem heading="Do I need to subscribe to a paid SLA?">
                      No, a paid SLA is not required. <strong>Paid SLA plans</strong> are designed for users who need <strong>fast delivery, faster response times, additional features, or more robust support</strong>.<br/><br/>
                      The free plan is fully functional for standard use; actually, it is the plan we used to manage all customers in the past up until now.
                    </HelpItem>

                    <HelpItem heading="How long does the SLA subscription last?">
                      Your SLA subscription lasts for the duration of your contract.
                    </HelpItem>

                    <HelpItem heading="Can I upgrade my SLA plan?">
                      Yes, you can upgrade your plan at any time. If you’re already on a paid plan and want to upgrade, please contact our support team for assistance.
                    </HelpItem>

                    <HelpItem heading="Can I downgrade my SLA plan?">
                      You may downgrade from <strong>BUSINESS</strong> to <strong>BUSINESS LITE</strong> only. Downgrades beyond this are not allowed in order to prevent misuse, manipulation of features, and throttling of service levels.
                    </HelpItem>

                    <HelpItem heading="How are “units” or tracks calculated?">
                      <strong>One unit</strong> equals <strong>one song or track</strong>. <strong>Units</strong> are only counted in the <strong>month they are created.</strong><br/><br/>
                      <strong>For example:</strong><br/>
                      &bull; If you upload <strong>100 songs in June</strong>, you’ll be charged for <strong>100 units in June</strong>.<br/>
                      &bull; If you upload <strong>5 songs in July</strong>, you’ll be charged for <strong>5 units in July</strong>.<br/><br/>
                      There is no recurring fee for previously uploaded songs — charges apply only to new uploads within each month. The unit counter resets monthly.
                    </HelpItem>

                    <HelpItem heading="What is the difference between GOLD PLUS Weekly and other plans?"> 
                      <strong>GOLD PLUS</strong> is our <strong>only weekly plan</strong>, designed for the most advanced users with high-priority needs or special requirements. It offers the <strong>fastest support, premium features</strong>, and <strong>flexibility</strong> without committing to a monthly subscription.<br/>
                      You can choose from any of our available plans whether it’s the <strong>STANDARD (Free) monthly plan</strong>, one of the <strong>monthly paid plans</strong>, or the <strong>GOLD PLUS weekly plan</strong>, depending on your needs and usage intensity.
                    </HelpItem>
                      
                    <br/>

                    <p><strong>For more questions please visit the dedicated WHITE LABEL and REST API premium help center.</strong></p>

                    <br/>

                    <strong>Note:</strong>
                    <ul>
                      <li>All timeframes mentioned are valid <strong>ONLY during business days (Monday – Friday)</strong>, excluding weekends and public holidays.</li>
                      <li>It may take <strong>up to 5 business days</strong> to activate your SLA after subscription.</li>
                    </ul>
                </div>
            </CardBody>
        </Card>
        {
          (currentUser.is_premium_user || currentUser.is_employee_account) && <ZapierBot />
        }
    </>
  );
};

export default ServiceLevelAgreement;